import {
  PendingNodeTask,
  PENDING_NODE_TASKS_KEY,
  PENDING_TASK_TTL_MS,
} from '../types/pendingTask';

function readTasks(): PendingNodeTask[] {
  if (typeof localStorage === 'undefined') {
    return [];
  }

  try {
    const raw = localStorage.getItem(PENDING_NODE_TASKS_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistTasks(tasks: PendingNodeTask[]): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(PENDING_NODE_TASKS_KEY, JSON.stringify(tasks));
  } catch {
    // Ignore storage errors (e.g. privacy mode).
  }
}

function filterExpired(tasks: PendingNodeTask[]): PendingNodeTask[] {
  const now = Date.now();
  return tasks.filter(
    (task) => task.createdAt + PENDING_TASK_TTL_MS >= now,
  );
}

function readWithCleanup(): PendingNodeTask[] {
  const tasks = readTasks();
  const filtered = filterExpired(tasks);

  if (filtered.length !== tasks.length) {
    persistTasks(filtered);
  }

  return filtered;
}

/** 获取所有待恢复任务 */
export function getAllPendingTasks(): PendingNodeTask[] {
  return readWithCleanup();
}

/** 根据画板ID获取任务 */
export function getPendingTasksByNodeGraph(
  nodeGraphId: string,
): PendingNodeTask[] {
  const tasks = readWithCleanup();
  return tasks.filter((task) => task.nodeGraphId === nodeGraphId);
}

/** 添加或更新任务记录（以 nodeGraphId:nodeId 为唯一键） */
export function upsertPendingTask(task: PendingNodeTask): void {
  const tasks = readWithCleanup();
  const updatedAt = Date.now();
  const existingIndex = tasks.findIndex(
    (item) =>
      item.nodeGraphId === task.nodeGraphId && item.nodeId === task.nodeId,
  );

  if (existingIndex !== -1) {
    const existing = tasks[existingIndex];
    tasks[existingIndex] = {
      ...existing,
      ...task,
      createdAt: existing.createdAt,
      updatedAt,
    };
  } else {
    tasks.push({ ...task, updatedAt });
  }

  persistTasks(tasks);
}

/** 删除任务记录 */
export function removePendingTask(
  nodeGraphId: string,
  nodeId: string,
): void {
  const tasks = readWithCleanup();
  const filtered = tasks.filter(
    (task) => !(task.nodeGraphId === nodeGraphId && task.nodeId === nodeId),
  );

  if (filtered.length !== tasks.length) {
    persistTasks(filtered);
  }
}

/** 清理过期记录（createdAt + TTL < now） */
export function cleanupExpiredTasks(): void {
  const tasks = readTasks();
  const filtered = filterExpired(tasks);

  if (filtered.length !== tasks.length) {
    persistTasks(filtered);
  }
}
